import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ActualServer, Budget, Account, STORAGE_KEYS, API_BASE_URL } from '../types';
import { auth } from '../utils/firebase';

/**
 * Get the current user's ID token for API authentication
 */
async function getIdToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken();
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

export const useServerManagement = () => {
  const { t } = useTranslation();
  const [savedServers, setSavedServers] = useState<ActualServer[]>([]);
  const [activeServerId, setActiveServerId] = useState<string | null>(null);

  // Initialize and load saved servers on startup
  useEffect(() => {
    const loadSavedServers = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.SERVERS);
        const servers = stored ? JSON.parse(stored) : [];
        setSavedServers(servers);

        // Load active server
        const activeId = localStorage.getItem(STORAGE_KEYS.ACTIVE_SERVER);
        if (activeId && servers.find((s: ActualServer) => s.id === activeId)) {
          setActiveServerId(activeId);
        } else if (servers.length > 0) {
          // Set first server as active if no active server or active server not found
          setActiveServerId(servers[0].id);
          localStorage.setItem(STORAGE_KEYS.ACTIVE_SERVER, servers[0].id);
        }
      } catch (error) {
        console.error('Failed to load saved servers:', error);
      }
    };

    loadSavedServers();
  }, []);

  // Get active server object
  const getActiveServer = useCallback((): ActualServer | null => {
    return savedServers.find(server => server.id === activeServerId) || null;
  }, [savedServers, activeServerId]);

  // Save servers to localStorage
  const saveServers = (servers: ActualServer[]) => {
    try {
      localStorage.setItem(STORAGE_KEYS.SERVERS, JSON.stringify(servers));
      setSavedServers(servers);

      // Handle active server management
      if (servers.length === 0) {
        setActiveServerId(null);
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_SERVER);
      } else if (!activeServerId || !servers.find(s => s.id === activeServerId)) {
        // Set first server as active if current active server is removed
        setActiveServerId(servers[0].id);
        localStorage.setItem(STORAGE_KEYS.ACTIVE_SERVER, servers[0].id);
      }
    } catch (error) {
      console.error('Failed to save servers:', error);
    }
  };

  // Set active server
  const setActiveServer = (serverId: string) => {
    setActiveServerId(serverId);
    localStorage.setItem(STORAGE_KEYS.ACTIVE_SERVER, serverId);
  };

  // Remove a saved server
  const removeServer = (serverId: string) => {
    const updatedServers = savedServers.filter(server => server.id !== serverId);
    saveServers(updatedServers);
  };

  // Remove a saved budget password
  const removeBudgetPassword = (serverId: string, budgetId: string) => {
    const updatedServers = savedServers.map(server => {
      if (server.id === serverId && server.budgetPasswords) {
        const updatedPasswords = { ...server.budgetPasswords };
        delete updatedPasswords[budgetId];
        return { 
          ...server, 
          budgetPasswords: Object.keys(updatedPasswords).length > 0 ? updatedPasswords : undefined 
        };
      }
      return server;
    });
    saveServers(updatedServers);
  };

  // Save last used configuration
  const saveLastUsedConfig = (serverId: string, budgetId: string, accountId: string, budgetPassword?: string) => {
    try {
      const config = { serverId, budgetId, accountId };
      localStorage.setItem(STORAGE_KEYS.LAST_USED, JSON.stringify(config));
      
      // Update the server's last used settings and budget password
      const updatedServers = savedServers.map(server => {
        if (server.id === serverId) {
          const updatedServer = { 
            ...server, 
            lastUsedBudgetId: budgetId, 
            lastUsedAccountId: accountId 
          };
          
          // Update budget password if provided
          if (budgetPassword) {
            updatedServer.budgetPasswords = {
              ...updatedServer.budgetPasswords,
              [budgetId]: budgetPassword
            };
          }
          
          return updatedServer;
        }
        return server;
      });
      saveServers(updatedServers);
    } catch (error) {
      console.error('Failed to save last used config:', error);
    }
  };

  // Validate budget access (test connection and get budget info)
  const validateBudgetAccess = async (server: ActualServer, budgetId: string, budgetPassword?: string): Promise<{ 
    success: boolean; 
    needsPassword: boolean; 
    error?: string 
  }> => {
    try {
      const requestBody: Record<string, string> = {
        serverURL: server.url,
        password: server.password,
        budgetId: budgetId,
      };

      if (budgetPassword) {
        requestBody.budgetPassword = budgetPassword;
      }

      const response = await fetch(`${API_BASE_URL}/get-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.needsBudgetPassword) {
          return { success: false, needsPassword: true };
        }
        return { success: false, needsPassword: false, error: errorData.error || t('failedToValidateBudgetAccess', { ns: 'serverManagement' }) };
      }

      return { success: true, needsPassword: false };
    } catch (error) {
      return { success: false, needsPassword: false, error: error instanceof Error ? error.message : t('failedToConnect', { ns: 'serverManagement' }) };
    }
  };

  // Load budgets for a server (used for changing default budget)
  const loadBudgetsForServer = async (server: ActualServer, forceRefresh = false): Promise<Budget[]> => {
    const now = Date.now();
    const cacheMaxAge = 5 * 60 * 1000; // 5 minutes
    
    // Use cached budgets if available and not expired, unless force refresh
    if (!forceRefresh && server.cachedBudgets && server.budgetsCachedAt && 
        (now - server.budgetsCachedAt) < cacheMaxAge) {
      return server.cachedBudgets;
    }

    // Get authentication token
    const token = await getIdToken();
    if (!token) {
      throw new Error(t('authRequiredForServerManagement', { ns: 'auth' }));
    }

    let response;
    try {
      response = await fetch(`${API_BASE_URL}/auth-and-get-budgets`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          serverURL: server.url,
          password: server.password
        }),
      });
    } catch (networkError) {
      throw new Error(`${t('networkError', { ns: 'serverManagement' })}: ${networkError instanceof Error ? networkError.message : t('failedToConnect', { ns: 'serverManagement' })}`);
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
      throw new Error(errorData.error || t('failedToAuthenticate', { ns: 'serverManagement' }));
    }

    const data = await response.json();
    
    // Update server with cached budgets
    const updatedServers = savedServers.map(s => {
      if (s.id === server.id) {
        return {
          ...s,
          cachedBudgets: data.budgets,
          budgetsCachedAt: now
        };
      }
      return s;
    });
    saveServers(updatedServers);
    
    return data.budgets;
  };

  // Load accounts with caching and background refresh
  const loadAccountsForBudget = async (server: ActualServer, budgetId: string, budgetPassword?: string, forceRefresh = false, enableBackgroundRefresh = true): Promise<Account[]> => {
    const now = Date.now();
    const cacheMaxAge = 2 * 60 * 1000; // 2 minutes for accounts (shorter than budgets)
    
    // Use cached accounts if available and not expired, unless force refresh
    const cachedAccounts = server.cachedAccounts?.[budgetId];
    const cacheTime = server.accountsCachedAt?.[budgetId];
    
    if (!forceRefresh && cachedAccounts && cacheTime && (now - cacheTime) < cacheMaxAge) {
      // Start background refresh with error handling only if enabled
      if (enableBackgroundRefresh) {
        setTimeout(() => {
          loadAccountsForBudget(server, budgetId, budgetPassword, true).catch(error => {
            console.warn('Background refresh of accounts failed:', error);
            // Don't throw error for background refresh failures
          });
        }, 100);
      }
      return cachedAccounts;
    }

    const requestBody: any = {
      serverURL: server.url,
      password: server.password,
      budgetId: budgetId
    };

    if (budgetPassword) {
      requestBody.budgetPassword = budgetPassword;
    }

    let response;
    try {
      response = await fetch(`${API_BASE_URL}/get-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
    } catch (networkError) {
      // Handle network/fetch failures
      throw new Error(`Network error: ${networkError instanceof Error ? networkError.message : 'Failed to connect to server'}`);
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
      
      if (errorData.needsBudgetPassword) {
        throw new Error('NEEDS_PASSWORD');
      }
      
      throw new Error(errorData.error || 'Failed to get accounts');
    }

    const data = await response.json();
    
    // Update server with cached accounts
    const updatedServers = savedServers.map(s => {
      if (s.id === server.id) {
        return {
          ...s,
          cachedAccounts: {
            ...s.cachedAccounts,
            [budgetId]: data.accounts
          },
          accountsCachedAt: {
            ...s.accountsCachedAt,
            [budgetId]: now
          }
        };
      }
      return s;
    });
    saveServers(updatedServers);
    
    return data.accounts;
  };

  return {
    savedServers,
    activeServerId,
    getActiveServer,
    saveServers,
    setActiveServer,
    removeServer,
    removeBudgetPassword,
    saveLastUsedConfig,
    validateBudgetAccess,
    loadBudgetsForServer,
    loadAccountsForBudget
  };
}; 